import {
    CustomOptions,
    PVOptions,
    ShowOptions,
    TaskOptions,
    Weblog,
} from '@ks/weblogger';
import KwaiLog from '@kibt/weblogger/lib/log.browser';
import KwaiLogBridge from '@kibt/weblogger/lib/log.bridge';
import cookies from 'js-cookie';
import * as Yoda from '@yoda/bridge';
import { currentCountry } from '@/common/const';

import { LogData, LogTypeEnum } from './type';

// WebLogger内核SDK配置参数
const logConfig = {
    env: process.env.VUE_APP_LOG_TYPE || 'development',
    proto: 'v2',
    fps: false,
    yoda: Yoda,
};

const kpn = cookies.get('kpn') || 'KWAI_KTOK' || 'KWAI';

const baseOptions = {
    package_name: kpn,
    product: 41,
    h5_extra_attr: {
        county_name: {
            country:
                currentCountry === 'br'
                    ? 'BRA'
                    : (currentCountry || '').toUpperCase() || 'UNKNOWN',
            bucket: cookies.get('bucket') || 'UNKNOWN',
        },
    },
};

// refer: https://component.corp.kuaishou.com/docs/weblogger/views/docs/config.html
// export const webLogger = new Weblog(logConfig as any, baseOptions);

// 接入kibilogger
// refer: https://docs.corp.kuaishou.com/k/home/VTEZRDemqihg/fcACO3M1wQVXJg3jP4i3ubXfh
// refer: 2.0 https://docs.corp.kuaishou.com/k/home/VBZ6OV_KzkVM/fcADS14mONNr64vQBR9wJKe15?ro=false#section=h.n0heo3wqiksb

export let webLoggerInstance: KwaiLog | KwaiLogBridge | undefined;

webLoggerInstance = new KwaiLog(logConfig as any, baseOptions);

export const webLogger: Weblog = webLoggerInstance!.weblog;

/**
 * 生成上报参数
 * @param data 上报数据
 * @returns PVOptions | ShowOptions | TaskOptions | CustomOptions
 */
const generateLogParams = (
    data: LogData
): PVOptions | ShowOptions | TaskOptions | CustomOptions => {
    // PV曝光其他参数暂时不支持
    if (data.type === 'pv') {
        return {
            type: data.stateType || 'enter',
            page: data.name,
            params: data.value,
            eventId: data.eventId,
        } as PVOptions;
    }

    if (data.type === 'custom') {
        return {
            key: data.name,
            value: data.value,
            eventId: data.eventId,
        } as CustomOptions;
    }

    // click 和 show 事件
    const logParams: any = {
        action: data.name,
        params: data.value,
        eventId: data.eventId,
    };

    if (data.urlPage) {
        (logParams as any).urlPage = data.urlPage;
    }

    return logParams as ShowOptions | TaskOptions;
};

/**
 * 数据上报
 * @param data 上报数据
 * @param immediate 是否立即上报
 */
const logCollect = (data: LogData, immediate?: boolean) => {
    const { type } = data || {};

    // 不存在类型则不上报
    if (!type) {
        return;
    }

    const logParams = generateLogParams(data);
    const logType = LogTypeEnum[type];

    if (immediate) {
        webLogger.sendImmediately(logType, logParams as any);
    } else {
        webLogger.collect(logType, logParams as any);
    }
};

/**
 * 收集上报埋点，会通过setUrlPageParams方式获取page
 * @param data
 * @param immediate
 */
export const sendClickLog = (
    data: Omit<LogData, 'type'>,
    immediate?: boolean
) => {
    logCollect(
        {
            type: 'click',
            ...data,
        },
        immediate
    );
};

export const sendTaskLog = (
    data: Omit<LogData, 'type'>,
    immediate?: boolean
) => {
    logCollect(
        {
            type: 'task',
            ...data,
        },
        immediate
    );
};

export const immediateSendLog = (data: LogData) => {
    logCollect(data, true);
};
